import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import { ChevronsRightIcon, Minus, Plus, Download } from "lucide-react";
import {
  AddButton,
  CustomConfirmModal,
  PaginationDropdown,
  ThinDropdownWithSearch,
} from "../../common/GeneralComponent";
import { fetchData } from "../../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Loader from "../../../components/partials/Loader";
import * as XLSX from "xlsx";
import GeneralFooter from "../../common/GeneralFooter";
import PermissionButton from "../../common/PermissionButton";

const Tehsile = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user: loggedInUser } = useSelector((state) => state.auth);
  const permissions = loggedInUser?.user_permissions || [];

  const [tehsils, setTehsils] = useState([]);
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedUuid, setSelectedUuid] = useState(null);
  const [search, setSearch] = useState("");
  const [isFiltersVisible, setIsFiltersVisible] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [tempDistrict, setTempDistrict] = useState(null); // temp selection
  const [isExporting, setIsExporting] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    totalPages: 0,
  });
  const [loader, setLoader] = useState(true);

  const fetchDistricts = async () => {
    try {
      const response = await fetchData(
        `districts`,
        "GET",
        null,
        {},
        {},
        navigate,
        "/districts",
        dispatch
      );
      setDistricts(response?.data || []);
    } catch (error) {
      console.error("Error fetching districts:", error);
    }
  };

  const fetchTehsils = async () => {
    try {
      const response = await fetchData(
        "all-tehsils",
        "GET",
        null,
        {},
        {
          search: search || undefined,
          page: pagination.currentPage,
          limit: pagination.pageSize,
          district_id: selectedDistrict || undefined,
        },
        navigate,
        "/tehsils",
        dispatch
      );
      setLoader(false);
      setTehsils(response?.data || []);
      setPagination(response?.pagination || {});
    } catch (error) {
      setLoader(false);
      console.error("Error fetching tehsils:", error);
    }
  };

  const exportToExcel = async () => {
    try {
      setIsExporting(true);
      const queryParams = {
        search: search || undefined,
        district_id: selectedDistrict || undefined,
        page: 1,
        limit: 10000,
      };
      const response = await fetchData(
        "all-tehsils",
        "GET",
        null,
        {},
        queryParams,
        navigate,
        "/tehsils",
        dispatch
      );
      if (!response?.data || response?.data.length === 0) {
        toast.warning("No tehsils found to export");
        return;
      }
      const allTehsils = response?.data;
      const excelData = allTehsils.map((tehsil, index) => ({
        "Sr. No": index + 1,
        "Tehsil Name": tehsil.tehsil_name || "",
        "District Name": tehsil.district?.district_name || "",
        Status: tehsil.status === 1 ? "Active" : "Inactive",
        "Created At": tehsil.created_at
          ? new Date(tehsil.created_at).toLocaleDateString()
          : "",
        "Updated At": tehsil.updated_at
          ? new Date(tehsil.updated_at).toLocaleDateString()
          : "",
      }));
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(excelData);
      XLSX.utils.book_append_sheet(workbook, worksheet, "Tehsils");
      const fileName = `tehsils_export_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      XLSX.writeFile(workbook, fileName);
      toast.success(
        `Successfully exported ${allTehsils.length} tehsils to Excel`
      );
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to export tehsils to Excel");
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => {
    fetchDistricts();
  }, []);

  useEffect(() => {
    fetchTehsils();
  }, [pagination.currentPage, pagination.pageSize, search, selectedDistrict]);

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handlePageChange = (event, value) => {
    setPagination((prev) => ({ ...prev, currentPage: value }));
  };

  const handlePageSizeChange = (size) => {
    setPagination((prev) => ({ ...prev, pageSize: size, currentPage: 1 }));
  };

  const handleDelete = async () => {
    setModalVisible(false);
    try {
      await fetchData(
        `tehsils`,
        "DELETE",
        { tehsil_id: selectedUuid },
        {},
        {},
        navigate,
        "/tehsils",
        dispatch
      );
      setLoader(false);
      toast.success("Tehsil deleted successfully.");
      fetchTehsils();
    } catch (error) {
      setLoader(false);
      console.error("Error deleting Tehsil:", error);
      toast.error(
        error?.message || "An error occurred while deleting the Tehsil."
      );
    }
  };

  const showDeleteModal = (id) => {
    setSelectedUuid(id);
    setModalVisible(true);
  };

  const toggleFilters = () => {
    setIsFiltersVisible((prev) => !prev);
  };

  const customStyles = {
    headCells: {
      style: {
        border: "1px solid #f3f3f3",
        color: "black",
        fontWeight: "bold",
        fontSize: "1.075rem",
        textAlign: "center",
      },
    },
    cells: {
      style: {
        border: "1px solid #f3f3f3",
        textAlign: "center",
      },
    },
  };

  const clearFilters = () => {
    setTempDistrict(null);
    setSelectedDistrict(null);
    setDistricts([]);
    fetchDistricts();
    setSearch("");
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
  };
  const columns = [
    {
      name: <span className="text-[0.813rem]">Sr.No</span>,
      cell: (row, index) => (
        <span>
          {(pagination.currentPage - 1) * pagination.pageSize + index + 1}
        </span>
      ),
      width: "80px",
      sortable: false,
    },
    {
      name: <span className="text-[0.813rem]">Tehsil Name</span>,
      selector: (row) => row?.tehsil_name,
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">District Name</span>,
      selector: (row) => row?.district?.district_name,
      sortable: true,
    },
    {
      name: <span className="text-[0.813rem]">Status</span>,
      cell: (row) => {
        if (row.status === null || row.status === undefined) {
          return <span className="text-gray-400">-</span>;
        }
        return (
          <span
            className={`py-1 px-2 text-[0.85em] rounded ${
              row.status === 1
                ? "bg-success text-white"
                : "bg-red-500 text-white"
            }`}
          >
            {row.status === 1 ? "Active" : "In-Active"}
          </span>
        );
      },
    },
  ];

  if (
    permissions.includes("tehsil-update") ||
    permissions.includes("tehsil-delete")
  ) {
    columns.push({
      name: <span className="text-[0.813rem]">Action</span>,
      cell: (row) => (
        <div className="flex gap-2">
          <PermissionButton
            hasPermission={permissions.includes("tehsil-update")}
            to={`/tehsils/edit/${row.tehsil_id}`}
            faIcon="fa-solid fa-pen-to-square fa-lg"
            onlyIcon={true}
            enabledClass=""
            disabledClass="opacity-50 cursor-not-allowed"
          />
          <PermissionButton
            hasPermission={permissions.includes("tehsil-delete")}
            faIcon="fa fa-trash fa-lg text-primaryDark"
            onlyIcon
            onClick={() => showDeleteModal(row.tehsil_id)}
            className="p-1 rounded-full bg-gray-200 text-[#137e81] hover:text-white"
            enabledClass=""
            disabledClass="opacity-50 cursor-not-allowed bg-gray-300"
          />
        </div>
      ),
    });
  }

  return (
    <div>
      {loader && <Loader />}
      <div className="my-4 w-full flex flex-col justify-between space-y-3">
        <div className="flex lg:flex-row flex-col lg:justify-between justify-start lg:items-center items-start gap-3 w-full">
          <h1 className="lg:text-[1.475rem] text-[1rem] text-[#212529] font-[Bold]">
            Tehsil
          </h1>
          <div className="flex gap-3">
            {tehsils.length > 0 && (
              <button
                onClick={exportToExcel}
                disabled={isExporting}
                className={`flex items-center gap-2 px-4 py-1 text-sm bg-[#137e81] hover:bg-green-700 text-white rounded-md transition-colors ${
                  isExporting ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                <Download size={16} />
                {isExporting ? "Exporting..." : "Export Excel"}
              </button>
            )}
            <PermissionButton
              hasPermission={permissions.includes("tehsil-create")}
              to="add"
              faIcon="fa fa-plus"
              text="Add"
              className="flex items-center gap-2 py-[0.375rem] px-[0.75rem] rounded font-[InterLight] lg:text-[15px] text-[12px]"
              enabledClass="bg-[#be9609] text-white w-auto opacity-95 hover:opacity-85"
              disabledClass="bg-[#be9609] text-white opacity-50 cursor-not-allowed"
            />
          </div>
        </div>
        <Accordion>
          <AccordionSummary
            expandIcon={
              isFiltersVisible ? (
                <Minus color="#fff" size={14} />
              ) : (
                <Plus color="#fff" size={14} />
              )
            }
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{
              backgroundColor: "#137e81",
              color: "#fff !important",
              "& .MuiAccordionSummary-content": { color: "#fff !important" },
            }}
            onClick={toggleFilters}
          >
            Filters
          </AccordionSummary>
          <AccordionDetails>
            <div className="flex gap-5 w-full">
              <div className="w-[30%]">
                <ThinDropdownWithSearch required={false}
                  label="District"
                  id="district-dropdown"
                  options={districts?.map((dis) => ({
                    value: dis?.district_id || dis?.District_Id,
                    label: dis?.district_name || dis?.District_Name,
                  }))}
                  value={tempDistrict}
                  onChange={(value) => setTempDistrict(value)}
                />
              </div>
              <div className="flex gap-3 items-end">
                <button
                  onClick={clearFilters}
                  className="px-4 py-2 bg-[#be9609] text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Clear
                </button>
                <button
                  onClick={() => {
                    setSelectedDistrict(tempDistrict);
                    setPagination((prev) => ({ ...prev, currentPage: 1 }));
                  }}
                  className="px-4 py-2 bg-[#137e81] text-white rounded hover:bg-[#0f6b6e] transition-colors"
                >
                  Apply
                </button>
              </div>
            </div>
          </AccordionDetails>
        </Accordion>
        {/* <br /> */}
        <div className="bg-white rounded-lg shadow w-full lg:mb-[30px]">
          <div className="p-4 flex md:flex-row rounded-t-lg gradiant2 flex-col gap-3 justify-between md:items-center border-lightGray border-b">
            <h1 className="lg:text-[0.875rem] text-[13px] tracking-[0.0625rem] text-[#856800] font-[bold] uppercase">
              Tehsil List
            </h1>
          </div>
          <div className="p-[1.25rem] w-full">
            <div className="flex lg:flex-row flex-col lg:justify-between justify-start gap-2 lg:items-center items-start mb-4 w-full">
              <PaginationDropdown
                value={pagination.pageSize}
                onChange={handlePageSizeChange}
                options={[
                  { value: 10, label: "10" },
                  { value: 20, label: "20" },
                  { value: 30, label: "30" },
                ]}
              />
              <input
                onChange={handleSearchChange}
                placeholder="Search..."
                type="search"
                className="text-[Light] flex items-center bg-[#ebeef2] border-[#ebeef2] rounded-md px-3 py-1 outline-none"
              />
            </div>
            <DataTable
              columns={columns}
              persistTableHead
              data={tehsils}
              customStyles={customStyles}
              highlightOnHover
            />
            <GeneralFooter
              pagination={pagination}
              handlePageChange={handlePageChange}
              handlePageSizeChange={handlePageSizeChange}
              dataLength={tehsils.length}
            />
          </div>
        </div>

        <CustomConfirmModal
          isVisible={isModalVisible}
          onConfirm={handleDelete}
          onCancel={() => setModalVisible(false)}
          message="Are you sure you want to delete this tehsil?"
        />
      </div>
    </div>
  );
};

export default Tehsile;
